/**
 * AdminMonitorPage — Consolidated System Monitor Page
 *
 * Merges Application Logs and Audit Logs into a single tabbed interface,
 * with an optional System Status tab.
 *
 * Tab 1: Application Logs  (from AdminLogsPage.tsx)
 * Tab 2: Audit Logs        (from SystemConfiguration.tsx audit tab)
 * Tab 3: System Status     (uses SystemStatusIndicator expanded)
 *
 * Logs remain searchable, paginated, and downloadable.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Article as LogsIcon,
  Policy as AuditIcon,
  MonitorHeart as StatusIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchSystemHealth } from '../store/systemHealthSlice';
import PageHeader from '../components/PageHeader';
import SystemStatusIndicator from '../components/SystemStatusIndicator';
import logCaptureService from '../services/logCaptureService';
import logService, { LogEntry } from '../services/logService';
import systemConfigService, { AuditLogEntry, ConfigGroup } from '../services/systemConfigService';
import { typography, fontFamily } from '~/theme';

// ── Constants ────────────────────────────────────────────────────────

const LOG_LEVELS = ['ALL', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

const LEVEL_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error' | 'success'> = {
  DEBUG: 'default',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'error',
};

// ── TabPanel (lazy) ──────────────────────────────────────────────────

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {children}
    </Box>
  );
}

// =====================================================================
// Component
// =====================================================================

const AdminMonitorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';

  const [activeTab, setActiveTab] = useState(0);
  const [visited, setVisited] = useState<Record<number, boolean>>({ 0: true });

  const handleTabChange = (_: unknown, v: number) => {
    setActiveTab(v);
    setVisited((prev) => ({ ...prev, [v]: true }));
  };

  // Admin guard
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  // ==================================================================
  // TAB 0 — Application Logs
  // ==================================================================
  const [logSubTab, setLogSubTab] = useState(0); // 0=backend, 1=frontend
  const [logLevel, setLogLevel] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [backendLogs, setBackendLogs] = useState<LogEntry[]>([]);
  const [frontendLogs, setFrontendLogs] = useState<LogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchLogs = useCallback(async () => {
    if (!isAdmin) return;
    setLogLoading(true);
    setLogError(null);
    try {
      const filterLevel = logLevel === 'ALL' ? undefined : logLevel;
      if (logSubTab === 0) {
        const data = await logService.getBackendLogs(200, filterLevel);
        setBackendLogs(data);
      } else {
        await logCaptureService.flush().catch(() => { /* ignore flush errors */ });
        const data = await logService.getFrontendLogs(200, filterLevel);
        setFrontendLogs(data);
      }
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        'Failed to fetch logs';
      setLogError(msg);
    } finally {
      setLogLoading(false);
    }
  }, [isAdmin, logSubTab, logLevel]);

  useEffect(() => {
    if (visited[0]) fetchLogs();
  }, [visited, fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchLogs, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [backendLogs, frontendLogs]);

  const currentLogs = logSubTab === 0 ? backendLogs : frontendLogs;

  const downloadLogs = () => {
    const text = currentLogs
      .map((e) => `${e.timestamp} [${e.level}] ${e.module ? e.module + ': ' : ''}${e.message}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${logSubTab === 0 ? 'backend' : 'frontend'}_logs_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadFullLogs = async () => {
    try {
      if (logSubTab === 0) {
        await logService.downloadBackendLogFile();
      } else {
        await logService.downloadFrontendLogFile();
      }
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        'Failed to download full log file';
      setLogError(msg);
    }
  };

  // ==================================================================
  // TAB 1 — Audit Logs
  // ==================================================================
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditFilter, setAuditFilter] = useState('');
  const [auditGroupFilter, setAuditGroupFilter] = useState('ALL');
  const [auditLoading, setAuditLoading] = useState(false);
  const [metadataGroups, setMetadataGroups] = useState<ConfigGroup[]>([]);

  // Build key→group lookup from metadata
  const keyGroupMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const g of metadataGroups) {
      for (const sg of g.sub_groups) {
        for (const k of sg.keys) {
          map[k.key] = g.id;
        }
      }
    }
    return map;
  }, [metadataGroups]);

  const groupLabelMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const g of metadataGroups) {
      map[g.id] = g.label;
    }
    return map;
  }, [metadataGroups]);

  const fetchAuditLogs = useCallback(async () => {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const [data, groups] = await Promise.all([
        systemConfigService.getAuditLog(200),
        metadataGroups.length === 0 ? systemConfigService.getMetadata() : Promise.resolve(metadataGroups),
      ]);
      setAuditLogs(data);
      if (metadataGroups.length === 0) setMetadataGroups(groups);
    } catch {
      // silently fail — audit log is optional
    } finally {
      setAuditLoading(false);
    }
  }, [isAdmin, metadataGroups]);

  useEffect(() => {
    if (visited[1]) fetchAuditLogs();
  }, [visited, fetchAuditLogs]);

  const filteredAuditLogs = auditLogs.filter((log) => {
    if (auditFilter && !log.config_key.toLowerCase().includes(auditFilter.toLowerCase())) return false;
    if (auditGroupFilter !== 'ALL' && keyGroupMap[log.config_key] !== auditGroupFilter) return false;
    return true;
  });

  const downloadAuditLogs = () => {
    const rows = [
      ['Timestamp', 'Config Key', 'Change Type', 'Old Value', 'New Value', 'Changed By'].join('\t'),
      ...filteredAuditLogs.map((log) =>
        [
          new Date(log.changed_at).toLocaleString(),
          log.config_key,
          log.change_type,
          log.old_value || '-',
          log.new_value,
          `User ${log.changed_by}`,
        ].join('\t'),
      ),
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Access guard ───────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Only administrators can access this page.</Alert>
      </Box>
    );
  }

  // ==================================================================
  // RENDER
  // ==================================================================
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="System Monitor"
        subtitle="Application logs, audit trail, and system health"
        onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')}
        backLabel="Back"
      />

      {/* ── Tab bar ─────────────────────────────────────────────────── */}
      <Paper sx={{ flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<LogsIcon />} iconPosition="start" label="Application Logs" />
          <Tab
            icon={<AuditIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Audit Logs
                {auditLogs.length > 0 && (
                  <Chip label={auditLogs.length} size="small" sx={{ height: 18, fontSize: typography.caption.fontSize }} />
                )}
              </Box>
            }
          />
          <Tab icon={<StatusIcon />} iconPosition="start" label="System Status" />
        </Tabs>
      </Paper>

      {/* ============================================================ */}
      {/* TAB 0 — Application Logs                                     */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={0}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap',
          }}
        >
          <Tabs value={logSubTab} onChange={(_, v) => setLogSubTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Backend Logs" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Frontend Logs" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select value={logLevel} label="Level" onChange={(e) => setLogLevel(e.target.value)}>
              {LOG_LEVELS.map((l) => (
                <MenuItem key={l} value={l}>{l}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch size="small" checked={autoRefresh} onChange={(_, v) => setAutoRefresh(v)} />}
            label={<Typography variant="caption">Auto-refresh (5s)</Typography>}
          />

          {logLoading && <CircularProgress size={16} />}

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {currentLogs.length} entries
            </Typography>
            <Tooltip title="Download filtered logs shown on screen">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadLogs}
                disabled={currentLogs.length === 0}
              >
                Download
              </Button>
            </Tooltip>
            <Tooltip title="Download complete log file">
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={downloadFullLogs}
              >
                Download Full Log
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {logError && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{logError}</Alert>
        )}

        {/* Log entries */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1, overflow: 'auto',
            fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
            fontSize: typography.caption.fontSize, lineHeight: typography.body2.lineHeight, p: 1.5,
            bgcolor: '#1e1e2e', color: '#cdd6f4',
          }}
        >
          {currentLogs.length === 0 && !logLoading && (
            <Typography variant="body2" color="grey.500" sx={{ textAlign: 'center', mt: 4 }}>
              No log entries found.
            </Typography>
          )}
          {currentLogs.map((entry, idx) => (
            <Box
              key={idx}
              sx={{ display: 'flex', gap: 1, py: 0.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
            >
              <Typography
                component="span"
                sx={{ color: '#6c7086', fontSize: 'inherit', fontFamily: fontFamily.body, whiteSpace: 'nowrap' }}
              >
                {entry.timestamp}
              </Typography>
              <Chip
                label={entry.level}
                size="small"
                color={LEVEL_COLORS[entry.level] ?? 'default'}
                sx={{ height: 18, fontSize: typography.caption.fontSize, fontWeight: typography.label.fontWeight, minWidth: 60 }}
              />
              {entry.module && (
                <Typography
                  component="span"
                  sx={{ color: '#89b4fa', fontSize: 'inherit', fontFamily: fontFamily.body, whiteSpace: 'nowrap' }}
                >
                  {entry.module}
                </Typography>
              )}
              <Typography
                component="span"
                sx={{ color: '#cdd6f4', fontSize: 'inherit', fontFamily: fontFamily.body, wordBreak: 'break-word' }}
              >
                {entry.message}
              </Typography>
            </Box>
          ))}
        </Box>
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 1 — Audit Logs                                           */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Group</InputLabel>
              <Select value={auditGroupFilter} label="Group" onChange={(e) => setAuditGroupFilter(e.target.value)}>
                <MenuItem value="ALL">All Groups</MenuItem>
                {metadataGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Filter by Config Key"
              value={auditFilter}
              onChange={(e) => setAuditFilter(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            <Tooltip title="Refresh audit log">
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAuditLogs} disabled={auditLoading}>
                Refresh
              </Button>
            </Tooltip>
            <Tooltip title="Download audit log">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadAuditLogs}
                disabled={filteredAuditLogs.length === 0}
              >
                Download
              </Button>
            </Tooltip>
            {auditLoading && <CircularProgress size={16} />}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {filteredAuditLogs.length} of {auditLogs.length} entries
            </Typography>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Config Key</TableCell>
                  <TableCell>Change Type</TableCell>
                  <TableCell>Old Value</TableCell>
                  <TableCell>New Value</TableCell>
                  <TableCell>Changed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAuditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        {auditLoading ? 'Loading...' : 'No audit log entries found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAuditLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(log.changed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={groupLabelMap[keyGroupMap[log.config_key]] || 'Unknown'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: typography.caption.fontSize }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                          {log.config_key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.change_type}
                          size="small"
                          color={log.change_type === 'create' ? 'success' : 'primary'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.old_value || '-'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.new_value}
                      </TableCell>
                      <TableCell>User {log.changed_by}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      {/* ============================================================ */}
      {/* TAB 2 — System Status                                        */}
      {/* ============================================================ */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>Service Health</Typography>
            <Typography variant="caption" color="text.secondary">
              Auto-refreshes every 30 seconds
            </Typography>
            <Tooltip title="Refresh now">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => dispatch(fetchSystemHealth({ force: true }))}
              >
                Refresh
              </Button>
            </Tooltip>
          </Box>
          <Paper sx={{ p: 3, flex: 1 }}>
            <SystemStatusIndicator variant="expanded" pollInterval={30000} />
          </Paper>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default AdminMonitorPage;
